import colorsJson from '../assets/colors/colors.json';

const FALLBACK_LINE_COLOR = '#13670B';
const FALLBACK_BAR_FADE_BASE = '#13670B';

type ColorsSchema = {
  palette?: { charts?: { categorical_premium?: string[]; bar_fade_base?: string } };
};

/** Full categorical palette for line/scatter traces; cycles when traces exceed palette length. From colors.json. */
const LINE_COLORS: string[] = (() => {
  const data = colorsJson as ColorsSchema;
  const arr = data?.palette?.charts?.categorical_premium;
  if (Array.isArray(arr) && arr.length > 0) return arr;
  return [FALLBACK_LINE_COLOR];
})();

/** Base color for bar fade palette. From colors.json. */
const BAR_FADE_BASE: string = (() => {
  const data = colorsJson as ColorsSchema;
  const base = data?.palette?.charts?.bar_fade_base;
  return typeof base === 'string' && base ? base : FALLBACK_BAR_FADE_BASE;
})();

/** Generate a professional color fade palette from base color */
function generateColorFadePalette(baseColor: string = BAR_FADE_BASE, steps: number = 5): string[] {
  // Convert hex to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
  };

  // Convert RGB to hex
  const rgbToHex = (r: number, g: number, b: number): string => {
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('').toUpperCase();
  };

  const [r, g, b] = hexToRgb(baseColor);
  const palette: string[] = [];

  // Generate fade: start with base color, then progressively lighten
  for (let i = 0; i < steps; i++) {
    const ratio = i / (steps - 1);
    // Blend towards a light, professional white to create tints
    const fadedR = Math.round(r + (255 - r) * ratio * 0.6);
    const fadedG = Math.round(g + (255 - g) * ratio * 0.6);
    const fadedB = Math.round(b + (255 - b) * ratio * 0.6);
    palette.push(rgbToHex(fadedR, fadedG, fadedB));
  }

  return palette;
}

export type ColorMode = 'auto' | 'trace' | 'points' | 'colorscale';

export type TraceColorCaps = {
  /** Modes supported for this trace (does not include "auto"). */
  modes: Array<Exclude<ColorMode, 'auto'>>;
  /** For "points" mode: number of points (bars/slices/markers). */
  pointCount?: number;
};

export type TraceOverride = {
  baseHex: string;
  mode?: ColorMode; // default: 'auto'
};

function clampInt(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.trunc(n)));
}

function safeArrayLen(v: any): number | undefined {
  return Array.isArray(v) ? v.length : undefined;
}

export function getTracePointCount(t: any): number | undefined {
  if (!t) return undefined;
  // Bar: x/y arrays; Pie: values; Scatter markers: x/y arrays; Heatmap: z is 2D (not "points" in the same sense)
  return (
    safeArrayLen(t?.values) ??
    safeArrayLen(t?.y) ??
    safeArrayLen(t?.x) ??
    undefined
  );
}

export function getTraceColorCaps(t: any): TraceColorCaps {
  const type = t?.type ?? 'scatter';
  const mode = String(t?.mode ?? '');
  const pointCount = getTracePointCount(t);

  if (type === 'heatmap') return { modes: ['colorscale'] };
  if (type === 'bar') return { modes: ['points', 'trace'], pointCount };
  if (type === 'pie') return { modes: ['points', 'trace'], pointCount };
  if ((type === 'scatter' || type === 'scattergl') && mode.includes('lines')) return { modes: ['trace'] };
  if ((type === 'scatter' || type === 'scattergl') && mode.includes('markers')) return { modes: ['points', 'trace'], pointCount };

  // Safe fallback: at least a trace-wide color is generally supported.
  return { modes: ['trace'], pointCount };
}

export function pickDefaultMode(caps: TraceColorCaps): Exclude<ColorMode, 'auto'> {
  if (caps.modes.includes('colorscale')) return 'colorscale';
  if (caps.modes.includes('points')) return 'points';
  return 'trace';
}

export function buildPointColors(baseHex: string, n: number): string[] {
  const count = clampInt(n || 0, 0, 20000);
  if (count <= 0) return [];
  const pal = generateColorFadePalette(baseHex || BAR_FADE_BASE, Math.max(count, 5));
  // Ensure exact length
  return Array.from({ length: count }, (_, i) => pal[i % pal.length]);
}

export function buildColorscale(baseHex: string): Array<[number, string]> {
  const stops = generateColorFadePalette(baseHex || BAR_FADE_BASE, 6);
  const denom = Math.max(1, stops.length - 1);
  return stops.map((c, i) => [i / denom, c]);
}

export function applyTraceOverride(trace: any, override?: TraceOverride): any {
  if (!override?.baseHex) return trace;

  const caps = getTraceColorCaps(trace);
  const resolvedMode: Exclude<ColorMode, 'auto'> =
    override.mode && override.mode !== 'auto' ? (override.mode as any) : pickDefaultMode(caps);

  const next = { ...trace };
  const baseHex = override.baseHex;
  const type = next?.type ?? 'scatter';
  const mode = String(next?.mode ?? '');

  if (resolvedMode === 'colorscale') {
    if (type === 'heatmap') {
      next.colorscale = buildColorscale(baseHex);
    }
    return next;
  }

  if (resolvedMode === 'points') {
    const n = caps.pointCount ?? 0;
    const colors = buildPointColors(baseHex, n);

    if (type === 'pie') {
      next.marker = next.marker || {};
      next.marker.colors = colors;
      return next;
    }

    // Bar and scatter markers use marker.color arrays for per-point colors.
    next.marker = next.marker || {};
    next.marker.color = colors;
    return next;
  }

  // resolvedMode === 'trace'
  if ((type === 'scatter' || type === 'scattergl') && mode.includes('lines')) {
    next.line = { ...(next.line || {}), color: baseHex };
    next.marker = next.marker ? { ...next.marker, color: baseHex } : { color: baseHex };
    return next;
  }

  if (type === 'bar') {
    next.marker = { ...(next.marker || {}), color: baseHex };
    return next;
  }

  if (type === 'pie') {
    next.marker = next.marker || {};
    // Pie trace-wide color is ambiguous; default to per-slice with repeated baseHex if possible.
    const n = caps.pointCount ?? 0;
    next.marker.colors = n > 0 ? Array.from({ length: n }, () => baseHex) : [baseHex];
    return next;
  }

  // Default for scatter markers / others
  next.marker = next.marker ? { ...next.marker, color: baseHex } : { color: baseHex };
  return next;
}

/** Apply brand colors to Plotly figure in-place. Use at render time so colors always apply. */
function applyBrandColorsToFigure(fig: { data?: any[]; layout?: any }): void {
  if (!fig.layout) fig.layout = {};
  fig.layout.colorway = LINE_COLORS;
  const L = LINE_COLORS.length;
  (fig.data || []).forEach((t: any, i: number) => {
    // Special handling for bar charts - apply professional fade
    if (t.type === 'bar') {
      t.marker = t.marker || {};
      const numBars = (t.y && t.y.length) || (t.x && t.x.length) || 1;
      const fadePalette = generateColorFadePalette(BAR_FADE_BASE, Math.max(numBars, 5));

      if (Array.isArray(t.marker.color) && t.marker.color.length > 0) {
        // If colors array exists, map to fade palette
        t.marker.color = (t.marker.color as any[]).map((_: unknown, j: number) => fadePalette[j % fadePalette.length]);
      } else if (t.marker.color === undefined || typeof t.marker.color === 'string') {
        // Single color or undefined - create array of faded colors
        t.marker.color = Array.from({ length: numBars }, (_, j) => fadePalette[j % fadePalette.length]);
      }
    } else if (t.marker && Array.isArray(t.marker.colors)) {
      t.marker.colors = (t.marker.colors as any[]).map((_, j) => LINE_COLORS[j % L]);
    } else if (t.marker && (t.marker.color === undefined || typeof t.marker.color === 'string')) {
      t.marker.color = LINE_COLORS[i % L];
    } else if (t.marker && Array.isArray(t.marker.color)) {
      const uniq = [...new Set(t.marker.color as any[])];
      const map: Record<string, string> = {};
      uniq.forEach((u, j) => { map[String(u)] = LINE_COLORS[j % L]; });
      t.marker.color = (t.marker.color as any[]).map((x) => map[String(x)] ?? x);
    } else if (t.type === 'pie') {
      t.marker = t.marker || {};
      t.marker.colors = (t.values || []).map((_: unknown, j: number) => LINE_COLORS[j % L]);
    } else if (t.type === 'scatter' && !t.marker) {
      t.marker = { color: LINE_COLORS[i % L] };
    } else if (t.type === 'scatter' && t.marker && t.marker.color === undefined && !Array.isArray(t.marker.colors)) {
      t.marker.color = LINE_COLORS[i % L];
    }
    if (t.line && t.type !== 'bar') {
      if (t.line.color === undefined || typeof t.line.color === 'string') {
        t.line.color = LINE_COLORS[i % L];
      } else if (Array.isArray(t.line.color)) {
        const uniq = [...new Set(t.line.color as any[])];
        const map: Record<string, string> = {};
        uniq.forEach((u, j) => { map[String(u)] = LINE_COLORS[j % L]; });
        t.line.color = (t.line.color as any[]).map((x) => map[String(x)] ?? x);
      }
    }
  });
}

// function applyColorsToLineChart
// Main idea: at the time, the 13570B hex color is the only brand color used in both bar and line
// The bar chart works well with the fading effect we have. But the line chart needs to be treated differently in colors
// the minimum case of line chart is a single line with a single color. But at maximum case it would be 14 different colors in line
// I have colors.json file that contains the brand colors. I want to use the colors.json file to apply the colors to the line chart.
// but solving the problem of having 14 different colors in line chart. Make it looks like ranbow chart (which is a bit cheap)
/** Exported for color picker UI (same 7 colors as line chart default). */
export { LINE_COLORS as chartPaletteColors, applyBrandColorsToFigure };